import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import Joi from 'joi';

import {
    useGetAdminsQuery,
    useGetShareCategoriesQuery,
    useGetShareSubcategoriesQuery,
    useGetTunnelMessagesQuery,
    useSignOutMutation,
} from 'api/main';
import { useSendInvestorEligibilityMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import { logoutOpen } from 'reducers/user';
import {
    useConfirmSuspend,
    useTunnelNavigation,
    useCheckInvestmentFund,
    useProClient,
    useAllStepIds,
    useDialog,
    useTunnelChangeHandlers,
} from 'utils/hooks';
import {
    boolToStr,
    getCustomMinInvestment,
    getUserIdFromIri,
    isStatusOk,
    jsonParseSafe,
    validateSchema,
} from 'utils/helpers';
import { fundsOfOrigin, sourceOfFunds } from 'utils/formData';
import {
    investmentFunds,
    investorErrors,
    RoleNames,
    stepIDs,
    exceptionTierOptions,
    fundTypes,
    question3IneligibleMessage,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import EligibilityForm from './EligibilityForm';
import CompanyEligibilityForm from './CompanyEligibilityForm';
import EligibilityFCPR from './EligibilityFCPR';
import SubcategoryDialog from './SubcategoryDialog';
import ProClientOption from './ProClientOption';
import TunnelStep from '../../common/TunnelStep';
import TunnelBox from '../../common/TunnelBox';
import { inputSx } from './eligibilityStyles';
import { CustomTextField, SubmitButton, CustomSelect, ConfirmDialog, Loader } from 'components';
import { Box, RadioGroup, MenuItem, Typography, Link } from '@mui/material';

// Helper function to find category for a subcategory
const findCategoryForSubcategory = (subcategoryId, categories) => {
    return categories.find(cat => cat.shareSubcategories?.some(sc => sc.id === subcategoryId));
};

const renderOption = opt => (
    <MenuItem key={opt.name} value={opt.name}>
        {opt.name}
    </MenuItem>
);

const parseFundsOrigin = fundsOrigin => {
    const { choice, other } = jsonParseSafe(fundsOrigin, { choice: '', other: '' });
    return { choice, other };
};

const defaultOption2Values = {
    subscriptionRequestMotivation: '',
    internalContactUser: '',
    nameOfEntity: '',
    nameOfFIA: '',
    durationOfHolding: '',
    exemptionConditionsProof: [],
};

export const publicSharesIneligibleMessage = category => {
    if (!category) return '';

    const paragraph1 = category.maxInvestedAmount
        ? texts.SUM_RANGE_ELIGIBILITY(category)
        : texts.SUM_MIN_ELIGIBILITY(category);

    return (
        <React.Fragment>
            <Box display="block" component="span">
                {paragraph1}
            </Box>
            <br></br>
            <Box display="block" component="span">
                {texts.USE_CORRECT_AMOUNT} (
                <Link underline="hover" href="mailto:contact@peqan.fr">
                    contact@peqan.fr
                </Link>
                ){'.'}
            </Box>
        </React.Fragment>
    );
};

const schema = {
    other: Joi.any()
        .when('fundsOrigin', {
            is: 'Autre',
            then: Joi.string().required(),
            otherwise: Joi.any(),
        })
        .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    investingSum: Joi.number().required(),
    subscriptionRequestMotivation: Joi.any()
        .when('investorQualification', {
            is: 'step34.question_2.option_1',
            then: Joi.string().required(),
            otherwise: Joi.any().when('investorQualification', {
                is: 'step34_fpci_pm_real.question_18.option_1.label',
                then: Joi.string().required(),
                otherwise: Joi.any(),
            }),
        })
        .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    internalContactUser: Joi.any().when('investorQualification', {
        is: 'step34.question_2.option_2',
        then: Joi.number().required(),
        otherwise: Joi.any().when('investorQualification', {
            is: 'step34_fpci_pm_real.question_18.option_2.label',
            then: Joi.number().required(),
            otherwise: Joi.any(),
        }),
    }),
    durationOfHolding: Joi.any().when('investorQualification', {
        is: 'step34.question_2.option_3',
        then: Joi.string().required(),
        otherwise: Joi.any().when('investorQualification', {
            is: 'step34_fpci_pm_real.question_18.option_3.label',
            then: Joi.string().required(),
            otherwise: Joi.any(),
        }),
    }),
};

const Eligibility = props => {
    const { handleNextStep, userStep } = props;
    const dispatch = useDispatch();
    const navigateToStep = useTunnelNavigation();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const [sendInvestorEligibility, { isLoading: buttonLoader, isError, error, reset }] =
        useSendInvestorEligibilityMutation();
    const [signOut, { isLoading: signOutLoading }] = useSignOutMutation();
    const { data: shareCategories = [], isLoading: categoriesLoader } = useGetShareCategoriesQuery(
        { investmentFund: tunnelSubscription?.investmentFund?.id },
        { skip: !tunnelSubscription }
    );
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery(
            { 'shareCategory.investmentFund': tunnelSubscription?.investmentFund?.id },
            { skip: !tunnelSubscription }
        );
    const { data: messages } = useGetTunnelMessagesQuery();

    const isStepVerified = useMemo(() => !!userStep?.successfulAttemptsNumber, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const [proClientDialogOpen, setProClientDialogOpen] = useState(false);
    const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
    const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
    const [formError, setFormError] = useState('');

    const {
        open: proFPCIOpen,
        onOpen: handleOpenProFPCI,
        onClose: handleCloseProFPCI,
    } = useDialog();

    const isCompany = useMemo(
        () => tunnelSubscription?.role?.name === RoleNames.PM_PROFESSIONNEL,
        [tunnelSubscription]
    );
    const fundIsPro = useMemo(
        () =>
            (tunnelSubscription?.investmentFund.type === fundTypes.FPCI ||
                tunnelSubscription?.investmentFund.type === fundTypes.FPS) &&
            tunnelSubscription?.investmentFund.professional,
        [tunnelSubscription]
    );
    const isFCPR = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.FCPR,
        [tunnelSubscription]
    );
    const isFPCI = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.FPCI,
        [tunnelSubscription]
    );
    const isFPS = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.FPS,
        [tunnelSubscription]
    );
    const isSLP = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.SLP,
        [tunnelSubscription]
    );
    const isClub1 = useCheckInvestmentFund(investmentFunds.CLUB_DEAL_1);
    const isClub2 = useCheckInvestmentFund(investmentFunds.CLUB_DEAL_2);

    const sgpName = useMemo(
        () => tunnelSubscription?.investmentFund?.sgp?.name,
        [tunnelSubscription]
    );

    const handleConfirmSuspend = useConfirmSuspend();
    const allStepIds = useAllStepIds();

    const userAdequacy = useMemo(() => tunnelSubscription?.userAdequacy, [tunnelSubscription]);
    const investmentCharacteristics = useMemo(
        () => tunnelSubscription?.investmentCharacteristics,
        [tunnelSubscription]
    );
    const defaultForm = useMemo(
        () => ({
            investingSum: investmentCharacteristics?.investingSum ?? '',
            investorQualification: investmentCharacteristics?.investorQualification || '',
            fixedCriteria: jsonParseSafe(investmentCharacteristics?.fixedCriteria) || [],
            fundsSource: investmentCharacteristics?.fundsSource || sourceOfFunds[0].name,
            fundsOrigin:
                parseFundsOrigin(investmentCharacteristics?.fundsOrigin).choice ||
                fundsOfOrigin[0].name,
            other: parseFundsOrigin(investmentCharacteristics?.fundsOrigin).other || '',
            subscriptionRequestMotivation:
                investmentCharacteristics?.subscriptionRequestMotivation || '',
            internalContactUser:
                getUserIdFromIri(investmentCharacteristics?.internalContactUser) || '',
            nameOfEntity: investmentCharacteristics?.nameOfEntity || '',
            nameOfFIA: investmentCharacteristics?.nameOfFIA || '',
            durationOfHolding: investmentCharacteristics?.durationOfHolding ?? '',
            balanceSheetTotalDocuments: investmentCharacteristics?.balanceSheetTotalDocuments || [],
            netSalesOrRevenuesDocuments:
                investmentCharacteristics?.netSalesOrRevenuesDocuments || [],
            higherEquityDocuments: investmentCharacteristics?.higherEquityDocuments || [],
            exemptionConditionsProof: investmentCharacteristics?.exemptionConditionsProof
                ? [investmentCharacteristics?.exemptionConditionsProof]
                : [],
        }),
        [investmentCharacteristics]
    );

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        investorQualification: '',
        fixedCriteria: '',
        investingSum: '',
        other: '',
        subscriptionRequestMotivation: '',
        internalContactUser: '',
        nameOfEntity: '',
        nameOfFIA: '',
        durationOfHolding: '',
        exemptionConditionsProof: '',
    });

    const { data: adminList = [], isLoading: adminsLoader } = useGetAdminsQuery(
        {},
        {
            skip:
                !form.investorQualification.includes('step34.question_2') &&
                !form.investorQualification.includes('step34_fpci_pm_real.question_18'),
        }
    );

    const durations = useMemo(
        () =>
            messages &&
            Object.keys(messages).filter(message => message.includes('duration_of_holding')),
        [messages]
    );

    const skipMinInvestedAmount = useMemo(
        () => exceptionTierOptions.includes(form.investorQualification),
        [form.investorQualification]
    );

    const sumMatchingSubcategories = useMemo(
        () =>
            shareSubcategories.filter(
                sc =>
                    (+form.investingSum >= sc.minInvestedAmount || skipMinInvestedAmount) &&
                    (!sc.maxInvestedAmount || +form.investingSum <= sc.maxInvestedAmount)
            ),
        [form.investingSum, shareSubcategories, skipMinInvestedAmount]
    );

    const matchingSubcategories = useMemo(() => {
        const existingShareSubcategory = shareSubcategories.find(
            sc => sc.id === tunnelSubscription?.investmentCharacteristics?.shareSubcategory?.id
        );

        // Check if existing subcategory has a private category
        if (existingShareSubcategory) {
            const existingCategory = findCategoryForSubcategory(
                existingShareSubcategory.id,
                shareCategories
            );
            if (existingCategory?.private) {
                return [existingShareSubcategory];
            }
        }

        let filteredSubCategories;
        if (tunnelSubscription?.referralCreated) {
            // only public shares
            filteredSubCategories = sumMatchingSubcategories.filter(sc => {
                const category = findCategoryForSubcategory(sc.id, shareCategories);
                return (
                    category &&
                    !category.private &&
                    category.id === tunnelSubscription?.shareCategory?.id
                );
            });
        } else if (tunnelSubscription?.indirect) {
            if (
                tunnelSubscription?.distributorCompany?.publicCategoryCommercialization &&
                tunnelSubscription?.distributorCompany?.privateCategoryCommercialization
            ) {
                // both public and private
                filteredSubCategories = sumMatchingSubcategories.filter(sc => {
                    const category = findCategoryForSubcategory(sc.id, shareCategories);
                    return (
                        category &&
                        sc.accessibleToDistribution &&
                        category.id === tunnelSubscription?.shareCategory?.id
                    );
                });
            } else if (tunnelSubscription?.distributorCompany?.publicCategoryCommercialization) {
                // public only
                filteredSubCategories = sumMatchingSubcategories.filter(sc => {
                    const category = findCategoryForSubcategory(sc.id, shareCategories);
                    return (
                        category &&
                        sc.accessibleToDistribution &&
                        !category.private &&
                        category.id === tunnelSubscription?.shareCategory?.id
                    );
                });
            } else {
                // private only do not overlap
                filteredSubCategories = sumMatchingSubcategories.filter(sc => {
                    const category = findCategoryForSubcategory(sc.id, shareCategories);
                    return category && sc.accessibleToDistribution && category.private;
                });
            }
        } else {
            // only public shares
            filteredSubCategories = sumMatchingSubcategories.filter(sc => {
                const category = findCategoryForSubcategory(sc.id, shareCategories);
                return category && !category.private;
            });
        }

        if (skipMinInvestedAmount && filteredSubCategories.length > 0) {
            // Check if we have multiple subShareCategories when we apply minimum condition
            const allowedCategories = filteredSubCategories.filter(
                sc => +form.investingSum >= sc.minInvestedAmount
            );
            if (0 === allowedCategories.length) {
                const lowestMinInvestedAmount = filteredSubCategories.reduce(
                    (lowest, sc) => (sc.minInvestedAmount < lowest ? sc.minInvestedAmount : lowest),
                    filteredSubCategories[0].minInvestedAmount
                );
                filteredSubCategories = filteredSubCategories.filter(
                    sc => sc.minInvestedAmount === lowestMinInvestedAmount
                );
            } else {
                filteredSubCategories = allowedCategories;
            }
        }

        return filteredSubCategories;
    }, [
        shareSubcategories,
        shareCategories,
        form.investingSum,
        tunnelSubscription,
        skipMinInvestedAmount,
        sumMatchingSubcategories,
    ]);

    const shareSubcategory = useMemo(() => {
        const subscriptionSubcategoryId =
            tunnelSubscription?.investmentCharacteristics?.shareSubcategory?.id;
        return shareSubcategories.find(sc => sc.id === subscriptionSubcategoryId) || null;
    }, [tunnelSubscription, shareSubcategories]);

    const shareCategoryIneligibleMessage = useMemo(
        () => publicSharesIneligibleMessage(shareSubcategory),
        [shareSubcategory]
    );

    const defaultInvestorProData = useMemo(
        () => ({
            professionalClient: userAdequacy?.professionalClient || false,
            transactionsCompleted: boolToStr(userAdequacy?.transactionsCompleted),
            portfolioValue: boolToStr(userAdequacy?.portfolioValue),
            professionalPositionHeld: boolToStr(userAdequacy?.professionalPositionHeld),
            transactionsCompletedDocuments: userAdequacy?.transactionsCompletedDocuments || [],
            portfolioValueDocuments: userAdequacy?.portfolioValueDocuments || [],
            professionalPositionHeldDocuments:
                userAdequacy?.professionalPositionHeldDocuments || [],
        }),
        [userAdequacy]
    );

    const [investorProData, setInvestorProData] = useState(defaultInvestorProData);

    useEffect(() => setInvestorProData(defaultInvestorProData), [defaultInvestorProData]);

    const isProClient = useProClient(form.investorQualification);

    useEffect(() => setForm(defaultForm), [defaultForm]);

    const {
        investingSum,
        investorQualification,
        fixedCriteria,
        fundsSource,
        fundsOrigin,
        other,
        nameOfEntity,
        nameOfFIA,
    } = form;

    const investmentRange = useMemo(() => {
        let minimumInvestmentSum = shareSubcategory?.minInvestedAmount || 0;
        if (!tunnelSubscription.referralCreated) {
            // apply absolute min (min of all subcategories tied to the investment fund)
            // apply exceptions
            minimumInvestmentSum = getCustomMinInvestment(
                investorQualification,
                shareCategories.reduce((acc, crt) => acc.concat(crt.shareSubcategories), [])
            );
        } // otherwise min and max from subcategory apply (see initialization)

        return {
            min: minimumInvestmentSum || 0,
            max: shareSubcategory?.maxInvestedAmount || 0,
        };
    }, [shareSubcategory, tunnelSubscription, investorQualification, shareCategories]);

    const handleLogout = useCallback(() => {
        handleCloseProFPCI();
        dispatch(logoutOpen(true));
    }, [dispatch, handleCloseProFPCI]);

    const checkProClientStatus = useCallback(() => {
        if (isFCPR && investorQualification !== 'step34_fcpr_pp_real.question_2') return true;
        const isTransactionCompletedTrue = investorProData.transactionsCompleted === 'true';
        const isPortfolioValueTrue = investorProData.portfolioValue === 'true';
        const isProfessionalPositionHeldTrue = investorProData.professionalPositionHeld === 'true';
        const atLeastTwoTrue =
            (isTransactionCompletedTrue && isPortfolioValueTrue) ||
            (isTransactionCompletedTrue && isProfessionalPositionHeldTrue) ||
            (isPortfolioValueTrue && isProfessionalPositionHeldTrue);
        const missingRequiredDocuments =
            (isTransactionCompletedTrue &&
                investorProData.transactionsCompletedDocuments.length < 1) ||
            (isPortfolioValueTrue && investorProData.portfolioValueDocuments.length < 1) ||
            (isProfessionalPositionHeldTrue &&
                investorProData.professionalPositionHeldDocuments.length < 1);
        return (
            !investorProData.professionalClient ||
            (investorProData.professionalClient && atLeastTwoTrue && !missingRequiredDocuments)
        );
    }, [investorProData, investorQualification, isFCPR]);

    const allowSubmit = useMemo(
        () => investingSum && (fundIsPro || investorQualification),
        [investingSum, fundIsPro, investorQualification]
    );

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        if (isCompany) {
            if (
                investorQualification === 'step34_fpci_pm_real.question_16.label' &&
                fixedCriteria.length < 2
            ) {
                setErrors(prevState => ({
                    ...prevState,
                    fixedCriteria: texts.VOUS_DEVEZ_RESPONDRE_AU_MOINS_2_QUESTIONS,
                }));
                return false;
            }
        }

        if (
            investorQualification === 'step34.question_2.label' ||
            investorQualification === 'step34_fpci_pm_real.question_18.label'
        ) {
            setErrors(prevState => ({
                ...prevState,
                investorQualification: texts.CHOISIR_OPTION,
            }));
            return false;
        }

        if (
            (investorQualification?.includes('step34.question_2') ||
                investorQualification?.includes('step34_fpci_pm_real.question_18')) &&
            form.exemptionConditionsProof.length === 0
        ) {
            setErrors(prevState => ({
                ...prevState,
                exemptionConditionsProof: texts.JUSTIFICATIF_OBLIGATOIRE,
            }));
            return false;
        }

        if (
            (investorQualification === 'step34.question_2.option_3' ||
                investorQualification === 'step34_fpci_pm_real.question_18.option_3.label') &&
            !(nameOfEntity || nameOfFIA)
        ) {
            setErrors(prevState => ({
                ...prevState,
                nameOfEntity: !nameOfEntity ? texts.SAISIR_LE_CHAMP : '',
                nameOfFIA: !nameOfFIA ? texts.SAISIR_LE_CHAMP : '',
            }));
            return false;
        }

        return valid;
    }, [form, investorQualification, fixedCriteria, isCompany, nameOfEntity, nameOfFIA]);

    const handleChange =
        key =>
        ({ target: { value } }) => {
            setForm(prevState => ({
                ...prevState,
                [key]: key === 'internalContactUser' || key === 'investingSum' ? +value : value,
            }));
        };

    const handleExemptionConditionsProofChange = useCallback(
        e => {
            const file = e.target.files[0];
            setForm(prevState => ({ ...prevState, exemptionConditionsProof: [file] }));
        },
        [setForm]
    );

    const removeExemptionConditionsProof = useCallback(() => {
        setForm(prevState => ({ ...prevState, exemptionConditionsProof: [] }));
    }, [setForm]);

    const handleRadioButtons = useCallback(
        key =>
            ({ target: { value } }) => {
                setForm(prevState => ({
                    ...prevState,
                    [key]: value,
                    // clear data on hidden options
                    ...(!(
                        key.includes('step34.question_2') ||
                        key.includes('step34_fpci_pm_real.question_18')
                    ) && defaultOption2Values),
                    ...(!key.includes('step34_fpci_pm_real.question_16') && {
                        fixedCriteria: [],
                        balanceSheetTotalDocuments: [],
                        netSalesOrRevenuesDocuments: [],
                        higherEquityDocuments: [],
                    }),
                }));

                if (
                    value === 'step34.question_2.option_2' ||
                    value === 'step34_fpci_pm_real.question_18.option_2.label'
                ) {
                    setForm(prevState => ({
                        ...prevState,
                        internalContactUser: adminList?.[0]?.id || '',
                    }));
                }

                if (
                    value === 'step34.question_2.option_3' ||
                    value === 'step34_fpci_pm_real.question_18.option_3.label'
                ) {
                    setForm(prevState => ({ ...prevState, durationOfHolding: durations[0] }));
                }

                if (value !== 'step34_fcpr_pp_real.question_2' && isFCPR) {
                    setInvestorProData(oldValue => ({
                        ...oldValue,
                        portfolioValueDocuments: [],
                        transactionsCompletedDocuments: [],
                        professionalPositionHeldDocuments: [],
                        portfolioValue: '',
                        transactionsCompleted: '',
                        professionalPositionHeld: '',
                    }));
                }

                setInvestorProData(oldValue => ({
                    ...oldValue,
                    professionalClient: value === 'step34_fcpr_pp_real.question_2',
                }));
            },
        [isFCPR, durations, adminList]
    );

    const handleConfirmProClientDialog = useCallback(() => {
        navigateToStep(stepIDs.REGULATORY_DOCUMENTS);
    }, [navigateToStep]);

    const handleCallback = useCallback(
        afterSubmitCallback => () => {
            if (isCompany && isFCPR) {
                handleNextStep(true);
            } else if (isProClient) {
                setProClientDialogOpen(true);
            } else if (!isProClient) {
                const eligibilityIndex = allStepIds.findIndex(sId => sId === stepIDs.ELIGIBILITY);
                navigateToStep(allStepIds[eligibilityIndex + 1]);
            }
            afterSubmitCallback?.();
        },
        [isCompany, isFCPR, isProClient, navigateToStep, handleNextStep, allStepIds]
    );

    const handleCloseProClientDialog = useCallback(() => {
        setProClientDialogOpen(false);
        setSignOutConfirmOpen(true);
    }, []);

    const getRemovableFiles = useCallback(
        (defaultFiles, existingFiles) =>
            defaultFiles
                .filter(defaultFile => !existingFiles.find(file => file.id === defaultFile.id))
                .map(file => file.id),
        []
    );

    const getAllRemovableFiles = useCallback(() => {
        const balanceSheetTotalDocsToRemove = getRemovableFiles(
            defaultForm.balanceSheetTotalDocuments,
            form.balanceSheetTotalDocuments
        );
        const netSalesOrRevenuesDocumentsToRemove = getRemovableFiles(
            defaultForm.netSalesOrRevenuesDocuments,
            form.netSalesOrRevenuesDocuments
        );
        const higherEquityDocumentsToRemove = getRemovableFiles(
            defaultForm.higherEquityDocuments,
            form.higherEquityDocuments
        );
        const transactionsCompletedDocumentsToRemove = getRemovableFiles(
            defaultInvestorProData.transactionsCompletedDocuments,
            investorProData.transactionsCompletedDocuments
        );
        const portfolioValueDocumentsToRemove = getRemovableFiles(
            defaultInvestorProData.portfolioValueDocuments,
            investorProData.portfolioValueDocuments
        );
        const professionalPositionHeldDocumentsToRemove = getRemovableFiles(
            defaultInvestorProData.professionalPositionHeldDocuments,
            investorProData.professionalPositionHeldDocuments
        );

        return balanceSheetTotalDocsToRemove.concat(
            netSalesOrRevenuesDocumentsToRemove,
            higherEquityDocumentsToRemove,
            transactionsCompletedDocumentsToRemove,
            portfolioValueDocumentsToRemove,
            professionalPositionHeldDocumentsToRemove
        );
    }, [defaultForm, defaultInvestorProData, form, investorProData, getRemovableFiles]);

    const dispatchForm = useCallback(
        selectedSubcategory => afterSubmitCallback => {
            const filesToRemove = getAllRemovableFiles();
            const exemptionConditionsProofToRemove = getRemovableFiles(
                defaultForm.exemptionConditionsProof,
                form.exemptionConditionsProof
            );
            sendInvestorEligibility({
                payload: {
                    ...form,
                    ...investorProData,
                    subcategory: selectedSubcategory,
                    filesToRemove,
                    exemptionConditionsProofToRemove,
                },
                callback: handleCallback(afterSubmitCallback),
            });
        },
        [
            sendInvestorEligibility,
            form,
            defaultForm,
            getRemovableFiles,
            getAllRemovableFiles,
            handleCallback,
            investorProData,
        ]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            if (
                isFPCI &&
                !investorProData.professionalClient &&
                tunnelSubscription?.investmentFund?.professional
            ) {
                handleOpenProFPCI();
                return;
            }
            if (!checkProClientStatus()) {
                setFormError(texts.SURVEY_RESULTS_SUBMIT_CONDITION);
                return;
            }
            if (
                form.investingSum >= 100000 &&
                exceptionTierOptions.includes(form.investorQualification)
            ) {
                dispatch(
                    ineligibleSet({
                        open: true,
                        abandon: true,
                        message: texts.INCORRECT_INVESTMENT_AMOUNT,
                        title: texts.VEUILLEZ_CHOISIR_UNE_OPTION,
                    })
                );
                return;
            }
            if (formIsValid()) {
                // multiple categories match
                if (matchingSubcategories.length > 1 && tunnelSubscription.indirectUntransferred) {
                    // display dialog
                    setSubcategoryDialogOpen(true);
                } else if (isFCPR && sumMatchingSubcategories.length === 0) {
                    dispatch(
                        ineligibleSet({
                            open: true,
                            message: texts.SUM_NOT_ENOUGH,
                        })
                    );
                } else {
                    dispatchForm()(afterSubmitCallback);
                }
            }
        },
        [
            checkProClientStatus,
            form,
            investorProData,
            formIsValid,
            isFCPR,
            isFPCI,
            dispatch,
            matchingSubcategories,
            dispatchForm,
            tunnelSubscription,
            sumMatchingSubcategories,
            handleOpenProFPCI,
        ]
    );

    useEffect(() => {
        if (isError) {
            const errorMessage =
                error.message || error.data?.errors?.[0]?.message?.replace(/"\d*" /, '') || '';
            const investorQualification = form.investorQualification;
            const isReferred = tunnelSubscription.referralCreated;
            const fundName = tunnelSubscription.investmentFund.name;

            // syntax error
            if (error.status === 400) {
                if (
                    (errorMessage === investorErrors.ELIGIBILITY_INVALID &&
                        investorQualification === 'step34.question_4.label') ||
                    (investorQualification === 'step34_fpci_pm_real.question_20' &&
                        (isFPCI || isSLP || isFPS) &&
                        isCompany)
                ) {
                    dispatch(
                        ineligibleSet({
                            open: true,
                            abandon: false,
                            canConfirm: true,
                            message: texts.NOT_ADEQUATE_P1(fundName),
                            confirmMessage: texts.PAUSE_AND_WAIT,
                            onConfirm: handleConfirmSuspend,
                        })
                    );
                }
                if (
                    // 30k options when investment is >= 100k
                    (investorQualification.includes('step34.question_2.option_') ||
                        investorQualification.includes(
                            'step34_fpci_pm_real.question_18.option_'
                        )) &&
                    (isClub1 || isClub2)
                ) {
                    dispatch(
                        ineligibleSet({
                            open: true,
                            message: texts.SUM_NOT_ENOUGH,
                        })
                    );
                }
                if (
                    (investorQualification === 'step34_fpci_pm_real.question_19' &&
                        (isFPCI || isFPS)) ||
                    investorQualification === 'step34_fcpr_pp_real.question_3' ||
                    investorQualification === 'step34_fpci_pm_real.question_19'
                ) {
                    dispatch(
                        ineligibleSet({
                            open: true,
                            message: question3IneligibleMessage,
                            title: texts.CONTINUE_INSCRIPTION,
                            hasDialogActions: false,
                        })
                    );
                }
            } else if (error.status && ![400, 401, 403, 500].includes(error.status)) {
                if (
                    errorMessage === investorErrors.SUM_INVALID ||
                    (errorMessage.includes('category:') && error.status === 404) ||
                    (errorMessage.includes('shareSubcategory ') && error.status === 404)
                ) {
                    if (isReferred) {
                        dispatch(
                            ineligibleSet({
                                open: true,
                                abandon: true,
                                message: shareCategoryIneligibleMessage,
                                subMessage: '', // overwrite default subMessage
                                title: texts.VEUILLEZ_CHOISIR_UNE_OPTION,
                            })
                        );
                    } else if (
                        investorQualification === 'step34.question_3.label' ||
                        investorQualification === 'step34_fcpr_pp_real.question_3' ||
                        investorQualification === 'step34_fpci_pm_real.question_19'
                    ) {
                        dispatch(
                            ineligibleSet({
                                open: true,
                                message: question3IneligibleMessage,
                                title: texts.CONTINUE_INSCRIPTION,
                                hasDialogActions: false,
                            })
                        );
                    } else if (
                        investorQualification === 'step34.question_4.label' ||
                        investorQualification === 'step34_fpci_pm_real.question_20' ||
                        investorQualification ===
                            'step34_fpci_pm_real.question_18.option_1.label' ||
                        investorQualification ===
                            'step34_fpci_pm_real.question_18.option_2.label' ||
                        investorQualification === 'step34_fpci_pm_real.question_18.option_3.label'
                    ) {
                        dispatch(
                            ineligibleSet({
                                open: true,
                                message: texts.VOTRE_RESPONSE_NE_SEMBLENT,
                            })
                        );
                    } else {
                        dispatch(
                            ineligibleSet({
                                open: true,
                                message: texts.SUM_NOT_ENOUGH,
                            })
                        );
                    }
                } else if (
                    errorMessage === investorErrors.INVESTMENT_SERVICE_PROVIDER &&
                    (investorQualification === 'step34_fcpr_pp_real.question_3' ||
                        investorQualification === 'step34.question_3.label')
                ) {
                    dispatch(
                        ineligibleSet({
                            open: true,
                            title: texts.CONTINUE_INSCRIPTION,
                            message: question3IneligibleMessage,
                            hasDialogActions: false,
                        })
                    );
                }
            }

            // always highlight the investment sum error
            if (errorMessage === investorErrors.SUM_INVALID) {
                if (investingSum < investmentRange.min) {
                    setErrors(prevState => ({
                        ...prevState,
                        investingSum: texts.SUM_INFERIOR(investmentRange.min),
                    }));
                } else if (!!investmentRange.max && investingSum > investmentRange.max) {
                    setErrors(prevState => ({
                        ...prevState,
                        investingSum: texts.SUM_SUPERIOR(investmentRange.max),
                    }));
                }
            }

            reset();
        }
    }, [
        dispatch,
        isClub1,
        isClub2,
        isCompany,
        isError,
        isSLP,
        isFPCI,
        isFPS,
        error,
        reset,
        tunnelSubscription,
        shareCategoryIneligibleMessage,
        handleConfirmSuspend,
        form.investorQualification,
        investingSum,
        investmentRange,
    ]);

    // returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => {
        return !isEqual(defaultForm, form) || !isEqual(defaultInvestorProData, investorProData);
    }, [form, defaultForm, defaultInvestorProData, investorProData]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isStepVerified),
        [handleSubmit, checkChanges, isStepVerified, setTunnelChangesHandlers]
    );

    const stepSubtitle = useMemo(() => {
        if (isFCPR) {
            return (
                <React.Fragment>
                    <Typography variant="subtitle1">
                        {texts.ELIGIBILITEE_FCPR_SUBTITLE(isCompany)}
                    </Typography>
                    <Typography variant="subtitle1">{texts.FCPR}</Typography>
                </React.Fragment>
            );
        } else if (isSLP) {
            return isCompany
                ? texts.POUVOIR_SOUSCRIERE_SLP_PM(sgpName)
                : texts.POUVOIR_SOUSCRIERE_SLP_PP(sgpName);
        } else if (isClub2 && !isCompany) {
            return null;
        } else if (isFPS) {
            return isCompany
                ? texts.POUVOIR_SOUSCRIERE_FPS_PM(sgpName)
                : texts.POUVOIR_SOUSCRIERE_FPS_PP(sgpName);
        }

        return isCompany
            ? texts.POUVOIR_SOUSCRIERE_PM(sgpName)
            : texts.POUVOIR_SOUSCRIERE_PP(sgpName);
    }, [isFCPR, isFPS, isSLP, isCompany, isClub2, sgpName]);

    return (
        <TunnelStep
            title={fundIsPro && !isCompany ? texts.INVESTMENT : texts.ELIGIBILITY}
            subtitle={stepSubtitle}
        >
            <Loader loading={categoriesLoader || subcategoriesLoader || adminsLoader}>
                <ConfirmDialog
                    open={proClientDialogOpen}
                    handleConfirm={handleConfirmProClientDialog}
                    handleCancel={handleCloseProClientDialog}
                    handleClose={() => setProClientDialogOpen(false)}
                    title={texts.PRO_CLIENT_BY_NATURE}
                    confirmText={texts.CONSULT_JURIDIC_DOCUMENTATION}
                    cancelText={texts.CONTINUER_PLUS_TARD}
                >
                    <Typography>{texts.PRO_CLIENT_INFO_TEXT_1}</Typography>
                    <Typography>{texts.PRO_CLIENT_INFO_TEXT_2}</Typography>
                </ConfirmDialog>
                <ConfirmDialog
                    open={signOutConfirmOpen}
                    handleConfirm={() => signOut(true)}
                    handleClose={() => setSignOutConfirmOpen(false)}
                    loading={signOutLoading}
                    title={texts.DEJA_QUITTER}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                >
                    <Typography>{texts.POUVEZ_REVENIR}</Typography>
                    <Typography>{texts.VOUS_RECONNECTER}</Typography>
                </ConfirmDialog>
                <ConfirmDialog
                    open={proFPCIOpen}
                    handleConfirm={handleLogout}
                    handleClose={handleCloseProFPCI}
                    title={texts.PROFESSIONAL_FUND}
                    confirmText={texts.QUITTER}
                    cancelText={texts.GO_BACK}
                >
                    <Typography>{texts.PRO_FUND_RESERVED}</Typography>
                </ConfirmDialog>
                <SubcategoryDialog
                    loading={buttonLoader}
                    open={subcategoryDialogOpen}
                    onClose={() => setSubcategoryDialogOpen(false)}
                    onConfirm={dispatchForm}
                    subcategories={matchingSubcategories}
                />
                {(!fundIsPro || isCompany) && (
                    <TunnelBox>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                alignItems: 'center',
                                margin: '-30px',
                            }}
                        >
                            <RadioGroup
                                name="investorQualification"
                                value={investorQualification}
                                onChange={handleRadioButtons('investorQualification')}
                            >
                                {isFCPR && (
                                    <EligibilityFCPR
                                        messages={messages}
                                        investorProData={investorProData}
                                        setInvestorProData={setInvestorProData}
                                        investorQualification={form.investorQualification}
                                        formError={formError}
                                        setFormError={setFormError}
                                        tunnelSubscription={tunnelSubscription}
                                    >
                                        {isCompany && (
                                            <CompanyEligibilityForm
                                                handleRadioButtons={handleRadioButtons}
                                                messages={messages}
                                                form={form}
                                                setForm={setForm}
                                                defaultForm={defaultForm}
                                                errors={errors}
                                                isFCPR={isFCPR}
                                                isFPS={isFPS}
                                                sgpName={sgpName}
                                                fundIsPro={fundIsPro}
                                                isSLP={isSLP}
                                                handleChange={handleChange}
                                                adminList={adminList}
                                                durations={durations}
                                            />
                                        )}
                                    </EligibilityFCPR>
                                )}
                                {isCompany && !isFCPR && (
                                    <CompanyEligibilityForm
                                        handleRadioButtons={handleRadioButtons}
                                        messages={messages}
                                        form={form}
                                        setForm={setForm}
                                        defaultForm={defaultForm}
                                        errors={errors}
                                        isFCPR={isFCPR}
                                        isFPS={isFPS}
                                        sgpName={sgpName}
                                        fundIsPro={fundIsPro}
                                        removeExemptionConditionsProof={
                                            removeExemptionConditionsProof
                                        }
                                        handleExemptionConditionsProofChange={
                                            handleExemptionConditionsProofChange
                                        }
                                        isSLP={isSLP}
                                        handleChange={handleChange}
                                        adminList={adminList}
                                        durations={durations}
                                    />
                                )}
                                {!isCompany && !isFCPR && (
                                    <EligibilityForm
                                        form={form}
                                        messages={messages}
                                        errors={errors}
                                        sgpName={sgpName}
                                        handleRadioButtons={handleRadioButtons}
                                        handleChange={handleChange}
                                        removeExemptionConditionsProof={
                                            removeExemptionConditionsProof
                                        }
                                        handleExemptionConditionsProofChange={
                                            handleExemptionConditionsProofChange
                                        }
                                        adminList={adminList}
                                        durations={durations}
                                        isSLP={isSLP}
                                        isFPS={isFPS}
                                    />
                                )}
                            </RadioGroup>
                        </Box>
                    </TunnelBox>
                )}
                {!isFCPR && (
                    <TunnelBox>
                        <ProClientOption
                            investorProData={investorProData}
                            setInvestorProData={setInvestorProData}
                            formError={formError}
                            setFormError={setFormError}
                            tunnelSubscription={tunnelSubscription}
                        />
                    </TunnelBox>
                )}
                <TunnelBox>
                    <Box width="100%" padding="16px 30px 30px">
                        <CustomTextField
                            name="investingSum"
                            formControlProps={{ sx: inputSx }}
                            label={texts.MONTANT_DE_ENVISAGE}
                            placeholder={texts.MONTANT_PLACEHOLDER(investmentRange.min)}
                            value={investingSum}
                            onChange={handleChange('investingSum')}
                            error={errors.investingSum}
                            formatNumber
                        />
                        <CustomSelect
                            controlSx={inputSx}
                            label={texts.PROVENANCE_DES_FONDS}
                            onChange={handleChange('fundsSource')}
                            value={fundsSource}
                        >
                            {sourceOfFunds.map(renderOption)}
                        </CustomSelect>
                        <CustomSelect
                            controlSx={inputSx}
                            label={texts.ORIGINE_DES_FONDS}
                            onChange={handleChange('fundsOrigin')}
                            value={fundsOrigin}
                        >
                            {fundsOfOrigin.map(renderOption)}
                        </CustomSelect>
                        {fundsOrigin === 'Autre' && (
                            <CustomTextField
                                formControlProps={{ sx: inputSx }}
                                onChange={handleChange('other')}
                                value={other}
                                error={errors.other}
                            />
                        )}
                    </Box>
                </TunnelBox>
                <SubmitButton
                    text={isStepVerified && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                    loading={buttonLoader}
                    onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                    disabled={!allowSubmit || buttonLoader}
                />
            </Loader>
        </TunnelStep>
    );
};

Eligibility.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default Eligibility;
